import React, { useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '../ui/button';
import { config } from '../../config/env';
import { processFacebookCookies } from '../../utils/facebookCookies';
import { processSnapchatCookies } from '../../utils/snapchatCookies';
import { facebookLoginService } from '../../services/facebookLoginDetection';
import { useAuthenticatedRequest } from '../../lib/useAuthenticatedRequest';
import { AccountRequestError, accountRequestMessage } from '../../lib/accountRequest';
import { signInPathForDestination } from '../../lib/authRedirect';

interface PlaybookModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PlaybookModal: React.FC<PlaybookModalProps> = ({ isOpen, onClose }) =>
  isOpen ? <PlaybookOffer onClose={onClose} /> : null;

const PlaybookOffer: React.FC<Pick<PlaybookModalProps, 'onClose'>> = ({ onClose }) => {
  const request = useAuthenticatedRequest();
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  const bulletPoints = [
    "Avoid the common pitfalls that holds back 90% of individuals from monetizing",
    "Access the exact blueprint the top 1% of earners follow on YouTube and TikTok",
    "Discover what content social media platforms want to see but never tell you",
    "Learn the proven and tested strategies to consistently going viral at scale"
  ];

  const handlePurchase = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    setNeedsSignIn(false);
    try {
      const clippingGuidePriceId = process.env.REACT_APP_STRIPE_CLIPPING_GUIDE_PRICE_ID;
      if (!clippingGuidePriceId) throw new AccountRequestError('request', 'The guide checkout is temporarily unavailable. Please contact hello@topclip.ai.');
      await request(async ({ ownerId, accessToken, signal, assertCurrent }) => {
      // Generate event_id for Facebook CAPI/Pixel deduplication
      const eventId = window.crypto?.randomUUID?.() ?? ('checkout_' + Date.now() + Math.random().toString(36).slice(2))

      // Process Facebook cookies and handle fbclid from URL
      const fbCookies = processFacebookCookies()
      console.log('🍪 Facebook cookies for PDF purchase:', fbCookies)

      // Process Snapchat cookies and handle ScCid from URL
      const snapCookies = processSnapchatCookies()
      console.log('🟡 Snapchat cookies for PDF purchase:', snapCookies)

      // Detect Facebook login ID for enhanced CAPI matching
      console.log('🔍 Detecting Facebook login status for PDF purchase...')
      const facebookLoginId = await facebookLoginService.getFacebookLoginId(1000) // 1 second timeout
      if (facebookLoginId) {
        console.log('✅ Facebook login detected for PDF:', facebookLoginId)
      } else {
        console.log('❌ No Facebook login detected for PDF')
      }

      // Track InitiateCheckout with Facebook Pixel
      if (typeof (window as any).fbq !== 'undefined') {
        (window as any).fbq('track', 'InitiateCheckout', {
          content_type: 'product',
          content_ids: [clippingGuidePriceId],
          value: 19,
          currency: 'USD'
        }, { eventID: eventId })
      }

      // Use payment mode for one-time guide purchase with custom success URL and CAPI data
      const requestBody = {
        price_id: clippingGuidePriceId,
        user_id: ownerId,
        mode: 'payment',
        expected_amount: 1900,
        expected_currency: 'usd',
        success_url: `${window.location.origin}/success?pdf=purchased&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/`,
        // CAPI tracking parameters (Facebook and Snapchat)
        event_id: eventId,
        fbc: fbCookies.fbc,
        fbp: fbCookies.fbp,
        sc_click_id: snapCookies.sc_click_id,
        sc_cookie1: snapCookies.sc_cookie1,
        user_agent: navigator.userAgent,
        external_id: ownerId,
        fb_login_id: facebookLoginId,
      };

      assertCurrent();
      const response = await fetch(config.endpoints.stripe.createCheckout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      if (response.status === 401) throw new AccountRequestError('authentication', 'Please sign in again to purchase the guide.');
      if (!response.ok) throw new AccountRequestError('request', 'We could not open the $19 guide checkout. Please try again, or contact hello@topclip.ai.');
      const responseData = await response.json();
      assertCurrent();
      if (typeof responseData.checkout_url !== 'string' || !responseData.checkout_url.trim()) throw new Error('Missing checkout URL');
      window.location.href = responseData.checkout_url;
      // Leave a usable page behind for browser Back/tab restoration, rather
      // than restoring an open overlay over the FAQ link.
      onClose();
      });
    } catch (error) {
      if (!(error instanceof AccountRequestError && error.kind === 'canceled')) {
        setError(accountRequestMessage(error));
        setNeedsSignIn(error instanceof AccountRequestError && error.kind === 'authentication');
      }
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="playbook-title" className="bg-white rounded-3xl w-[calc(100%-2rem)] max-w-4xl max-h-[95vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative px-8 pt-10 pb-8 text-center">
          {/* Header */}
          <h2 id="playbook-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 text-center">
            Steal the Secret Playbook of Top Earners
          </h2>
          
          {/* Subheader */}
          <p className="text-base sm:text-lg text-gray-600 text-center max-w-2xl mx-auto">
            Why some creators make over <span className="font-bold">$20k+/month</span> while others struggle to<br />
            make $1 - revealed in this 18-page <span className="font-bold">insider guide</span>.
          </p>
        </div>

        {/* Content */}
        <div className="px-8 pb-10">
          {/* Bullet Points */}
          <div className="space-y-4 mb-10 max-w-2xl mx-auto">
            {bulletPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex items-center justify-center rounded-full bg-green-100 p-1.5 mt-0.5 flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" strokeWidth={3} />
                </div>
                <p className="text-sm sm:text-base text-gray-700">{point}</p>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div className="flex flex-col items-center justify-center mb-10">
            <div className="flex items-center gap-2">
              <span className="text-5xl font-bold text-gray-500 line-through mr-2">$99</span>
              <span className="text-5xl font-bold text-green-600">$19</span>
            </div>
            <span className="text-sm text-gray-500 mt-2">USD, one-time purchase</span>
          </div>

          {error && <div role="alert" className="mx-auto mb-4 max-w-2xl text-sm text-red-700"><p>{error}</p>
            {needsSignIn && <Button asChild variant="link"><a href={signInPathForDestination('/dashboard')}>Sign in again</a></Button>}
            <a className="inline-block min-h-11 py-3 text-blue-600 underline" href="mailto:hello@topclip.ai?subject=TopClip%20guide%20checkout%20help">Contact support</a>
          </div>}
          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {/* Left Button - White */}
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full h-12 text-blue-600 border-blue-600 hover:bg-blue-50 rounded-xl font-semibold"
            >
              I don't want to go viral
            </Button>

            {/* Right Button - Blue */}
            <Button
              onClick={handlePurchase}
              disabled={pending}
              aria-busy={pending}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
            >
              {pending ? 'Opening checkout...' : 'I want to go viral'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
